// ─────────────────────────────────────────────────────
// Social Page — Connected platforms, shared posts, analytics
// ─────────────────────────────────────────────────────
import { useEffect, useState } from "react";
import { ExternalLink, TrendingUp } from "lucide-react";
import { clsx } from "clsx";
import { api } from "../services/api";

interface PlatformInfo {
  id: string;
  name: string;
  emoji: string;
  color: string;
  connected: boolean;
  followers: string;
  posts: number;
  username?: string;
}

interface PostInfo {
  id: string;
  platform: string;
  emoji: string;
  title: string;
  likes: number;
  comments: number;
  time: string;
  url?: string;
}

const PLATFORM_META: Record<string, { name: string; emoji: string; color: string }> = {
  snapchat: { name: "Snapchat", emoji: "👻", color: "#FFFC00" },
  instagram: { name: "Instagram", emoji: "📸", color: "#E1306C" },
  tiktok: { name: "TikTok", emoji: "🎵", color: "#00F2EA" },
  pinterest: { name: "Pinterest", emoji: "📌", color: "#E60023" },
  x: { name: "X", emoji: "𝕏", color: "#FFF" },
  messages: { name: "Messages", emoji: "💬", color: "#34C759" },
};

export function SocialPage() {
  const [platforms, setPlatforms] = useState<PlatformInfo[]>([]);
  const [posts, setPosts] = useState<PostInfo[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadSocial() {
      try {
        const [accountsRes, historyRes] = await Promise.allSettled([
          api.getPlatforms(),
          api.getSharedPosts(),
        ]);

        // Build platform list from connected accounts + static defaults
        const connectedAccounts: Record<string, unknown>[] =
          accountsRes.status === "fulfilled" && Array.isArray(accountsRes.value)
            ? accountsRes.value
            : [];
        const connectedIds = new Set(connectedAccounts.map((a) => a.platform as string));
        const allPlatforms: PlatformInfo[] = Object.entries(PLATFORM_META).map(([id, meta]) => ({
          id,
          ...meta,
          connected: connectedIds.has(id),
          followers: "—",
          posts: 0,
          username: connectedAccounts.find((a) => a.platform as string === id)?.username as string | undefined,
        }));
        setPlatforms(allPlatforms);

        // Load share history
        const rawPosts: Record<string, unknown>[] =
          historyRes.status === "fulfilled" && Array.isArray(historyRes.value)
            ? historyRes.value
            : [];
        setPosts(
          rawPosts.map((p, i) => {
            const pid = (p.platform as string) || "instagram";
            const meta = PLATFORM_META[pid] || PLATFORM_META.instagram;
            return {
              id: (p.id as string) || String(i),
              platform: meta.name,
              emoji: meta.emoji,
              title: (p.content_type as string) || (p.caption as string) || "Shared Post",
              likes: (p.likes as number) || 0,
              comments: (p.comments as number) || 0,
              time: (p.shared_at as string) || "",
              url: (p.url as string) || undefined,
            };
          })
        );
      } catch {
        // graceful fallback
      } finally {
        setLoading(false);
      }
    }
    loadSocial();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-sxt-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const connectedCount = platforms.filter((p) => p.connected).length;
  const totalPosts = posts.length;

  return (
    <div>
      <h2 className="text-3xl font-bold mb-8">Social</h2>

      {/* Platform Cards */}
      <div className="grid grid-cols-3 gap-4 mb-8">
        {platforms.map((p) => (
          <div
            key={p.id}
            className={clsx(
              "bg-sxt-surface rounded-2xl p-5 border animate-fade-in",
              p.connected ? "border-sxt-border" : "border-dashed border-sxt-dim opacity-60"
            )}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: p.color + "20" }}
                >
                  <span className="text-xl">{p.emoji}</span>
                </div>
                <div>
                  <p className="text-sm font-semibold">{p.name}</p>
                  <p className="text-xs text-sxt-muted">
                    {p.connected ? `Connected${p.username ? ` • @${p.username}` : ""}` : "Not connected"}
                  </p>
                </div>
              </div>
              {!p.connected && (
                <button className="text-xs bg-sxt-primary/15 text-sxt-primary px-3 py-1.5 rounded-lg font-semibold hover:bg-sxt-primary/25 transition-colors">
                  Connect
                </button>
              )}
            </div>
            {p.connected && (
              <div className="flex gap-6 mt-3 pt-3 border-t border-sxt-border/50">
                <div>
                  <p className="text-lg font-bold">{p.followers}</p>
                  <p className="text-xs text-sxt-muted">Followers</p>
                </div>
                <div>
                  <p className="text-lg font-bold">{p.posts}</p>
                  <p className="text-xs text-sxt-muted">Posts</p>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Social Stats */}
      <div className="grid grid-cols-4 gap-4 mb-8">
        <div className="bg-sxt-surface rounded-2xl p-5 border text-center animate-fade-in">
          <TrendingUp className="w-6 h-6 text-sxt-primary mx-auto mb-2" />
          <p className="text-2xl font-bold">{connectedCount}</p>
          <p className="text-xs text-sxt-muted">Connected Platforms</p>
        </div>
        <div className="bg-sxt-surface rounded-2xl p-5 border text-center animate-fade-in">
          <p className="text-2xl font-bold">{totalPosts}</p>
          <p className="text-xs text-sxt-muted">Total Posts</p>
        </div>
        <div className="bg-sxt-surface rounded-2xl p-5 border text-center animate-fade-in">
          <p className="text-2xl font-bold">—</p>
          <p className="text-xs text-sxt-muted">Engagement Rate</p>
        </div>
        <div className="bg-sxt-surface rounded-2xl p-5 border text-center animate-fade-in">
          <p className="text-2xl font-bold">—</p>
          <p className="text-xs text-sxt-muted">Total Likes</p>
        </div>
      </div>

      {/* Recent Posts */}
      <h3 className="text-xl font-bold mb-4">Recent Posts</h3>
      <div className="bg-sxt-surface rounded-2xl border overflow-hidden">
        {posts.length === 0 ? (
          <p className="text-sxt-muted text-center py-8">No posts yet. Connect a platform and start sharing!</p>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b">
                <th className="text-left text-xs text-sxt-muted font-medium px-5 py-3">Post</th>
                <th className="text-left text-xs text-sxt-muted font-medium px-5 py-3">Platform</th>
                <th className="text-right text-xs text-sxt-muted font-medium px-5 py-3">Likes</th>
                <th className="text-right text-xs text-sxt-muted font-medium px-5 py-3">Comments</th>
                <th className="text-right text-xs text-sxt-muted font-medium px-5 py-3">Time</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {posts.map((post) => (
                <tr key={post.id} className="border-b border-sxt-border/50 hover:bg-sxt-border/20 transition-colors">
                  <td className="px-5 py-4 text-sm font-medium">{post.title}</td>
                  <td className="px-5 py-4">
                    <span className="text-sm">{post.emoji} {post.platform}</span>
                  </td>
                  <td className="px-5 py-4 text-sm text-right text-sxt-primary font-semibold">
                    {post.likes.toLocaleString()}
                  </td>
                  <td className="px-5 py-4 text-sm text-right text-sxt-muted">{post.comments}</td>
                  <td className="px-5 py-4 text-sm text-right text-sxt-dim">{post.time}</td>
                  <td className="px-5 py-4 text-right">
                    {post.url && (
                      <a href={post.url} target="_blank" rel="noopener noreferrer" className="text-sxt-dim hover:text-sxt-primary transition-colors">
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
